import { LinearProgress } from '@mui/material';
import React, { lazy } from 'react';
import { Navigate, RouteObject, useRoutes } from 'react-router-dom';

import paths from './paths';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const Auth = lazy(() => import('../pages/auth'));
const Verify = lazy(() => import('src/pages/verify'));
const UpdatePassword = lazy(() => import('../pages/update-password'));
const Logout = lazy(() => import('../pages/logout'));
const ErrorPageNotFound = lazy(() => import('../pages/404'));
const Checkout = lazy(() => import('../pages/checkout'));

const Dashboard = lazy(() => import('../pages/dashboard'));
const SplitPayments = lazy(() => import('../pages/split-payments'));
const SplitPaymentsTransactions = lazy(() => import('../pages/split-payments/transactions'));
const SplitPaymentsPayees = lazy(() => import('../pages/split-payments/payees'));
const SplitPaymentsPayeeDetails = lazy(() => import('../pages/split-payments/payee-details'));
const SplitPaymentsRules = lazy(() => import('../pages/split-payments/rules'));
const SplitPaymentsRuleDetails = lazy(() => import('src/pages/split-payments/rule-details'));
const SplitPaymentsPayouts = lazy(() => import('src/pages/split-payments/payouts'));
const Chat = lazy(() => import('../pages/chat'));
const Onboarding = lazy(() => import('../pages/onboarding'));
const Sandbox = lazy(() => import('../pages/sandbox'));
const Profile = lazy(() => import('../pages/profile'));
const PaymentAnalytics = lazy(
  () => import('../pages/dashboard/payment-analytics')
);
const OnlinePaymentsTransactions = lazy(
  () => import('src/pages/online-payments/transactions')
);

const publicRoutes: RouteObject[] = [
  {
    path: paths.auth,
    element: <PublicRoute />,
    children: [
      {
        path: paths.auth,
        element: <Navigate to={paths.signIn} />,
      },
      {
        path: paths.signUp,
        element: <Auth type="signUp" />,
      },
      {
        path: paths.signIn,
        element: <Auth type="signIn" />,
      },
      {
        path: paths.otp,
        element: <Auth type="otp" />,
      },
      {
        path: paths.forgotPassword,
        element: <Auth type="forgotPassword" />,
      },
      {
        path: paths.updatePassword,
        element: <UpdatePassword />,
      },
      {
        path: paths.verify,
        element: <Verify />,
      },
    ],
  },
  {
    path: paths.checkout,
    element: <Checkout />,
  },
  {
    path: paths.notFound,
    element: <ErrorPageNotFound />,
  },
];

const protectedRoutes: RouteObject[] = [
  {
    element: <PrivateRoute />,
    children: [
      {
        index: true,
        path: paths.main,
        element: <Dashboard />,
      },
      {
        path: paths.splitPayments,
        element: <SplitPayments />,
      },
      {
        path: paths.splitPaymentsTransactions,
        element: <SplitPaymentsTransactions />,
      },
      {
        path: paths.splitPaymentsPayees,
        element: <SplitPaymentsPayees />,
      },
      {
        path: paths.splitPaymentsPayeeDetails,
        element: <SplitPaymentsPayeeDetails />,
      },
      {
        path: paths.splitPaymentsRules,
        element: <SplitPaymentsRules />,
      },
      {
        path: paths.splitPaymentsRuleDetails,
        element: <SplitPaymentsRuleDetails />,
      },
      {
        path: paths.splitPaymentsPayouts,
        element: <SplitPaymentsPayouts />,
      },
      {
        path: paths.onlinePaymentsTransactions,
        element: <OnlinePaymentsTransactions />,
      },
      {
        path: paths.chat,
        element: <Chat />,
      },
      {
        path: paths.profile,
        element: <Profile />,
      },
      {
        path: paths.logout,
        element: <Logout />,
      },
      {
        path: paths.paymentAnalytics,
        element: <PaymentAnalytics />,
      },
      {
        path: paths.onboarding,
        element: <Onboarding />,
      },
      {
        path: paths.sandbox,
        element: <Sandbox />,
      },
    ],
  },
];

const routes = [...publicRoutes, ...protectedRoutes];

export const Routes = () => {
  const element = useRoutes(routes);
  return (
    <React.Suspense
      fallback={<LinearProgress color="secondary" sx={{ width: '100%' }} />}
    >
      {element}
    </React.Suspense>
  );
};
